import styled from "@emotion/styled";
import { FC, useEffect, useRef } from "react";
import { Field, FieldRenderProps, useField } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import { Add, Close } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Input,
  RadiusToken,
  SpacingToken,
} from "@livechat/design-system-react-components";

import { Divider } from "components/divider/Divider";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { SelectionBlockType } from "store/entities/contact-forms/interfaces";
import { withVar } from "themes/helpers";

import { RequiredErrorField } from "./parts/RequiredErrorField";

type Props = {
  name: string;
  blockType: SelectionBlockType;
};

const OptionField: FC<FieldRenderProps<string>> = ({ input, meta, label, onRemove, hideRemove }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);
  return (
    <OptionFieldWrapper>
      {!hideRemove && (
        <Button
          icon={<Close />}
          kind="plain"
          size="xcompact"
          onClick={onRemove}
          css={deleteButtonStyles}
          data-testid="remove-option-button"
        />
      )}
      <FormField labelText={label} error={meta.error} css={formFieldStyles}>
        <Input {...input} error={meta.error} ref={inputRef} />
      </FormField>
    </OptionFieldWrapper>
  );
};

export const SelectionOptionsBlock: FC<Props> = ({ name, blockType }) => {
  const isRequired = useField(name).input.value.required;

  const label = {
    select: "Dropdown list name",
    radio: "Single-choice list name",
    checkbox: "Multiple-choice list name",
  }[blockType];

  return (
    <Flex column>
      <Field name={`${name}.value`}>
        {({ input, meta }) => (
          <FormField labelText={label} error={meta.error}>
            <Input {...input} error={meta.error} />
          </FormField>
        )}
      </Field>
      <FieldArray name={`${name}.options`}>
        {({ fields }) => (
          <Flex column>
            {fields.map((name, index) => {
              return (
                <Field
                  key={`${name}-${index}`}
                  name={`${name}.value`}
                  render={OptionField}
                  hideRemove={index == 0}
                  label={
                    index == 0 && blockType == "select"
                      ? "Initial content"
                      : `Choice #${blockType == "select" ? index : index + 1}`
                  }
                  onRemove={() => fields.remove(index)}
                />
              );
            })}
            <Button
              icon={<Add />}
              kind="secondary"
              size="compact"
              onClick={() => fields.push({ value: "" })}
              css={addButtonStyles}
            >
              Add choice
            </Button>
          </Flex>
        )}
      </FieldArray>
      {isRequired && (
        <div>
          <Block marginTop={withVar(SpacingToken.Spacing4)} />
          <Divider />
          <Field name={`${name}.requiredError`} render={RequiredErrorField} blockType={blockType} />
        </div>
      )}
    </Flex>
  );
};

const OptionFieldWrapper = styled.div`
  position: relative;
  border-left: 1px dashed ${withVar(DesignToken.BorderBasicPrimary)};
`;

const deleteButtonStyles = {
  position: "absolute" as const,
  right: "12px",
  top: "12px",
};

const formFieldStyles = {
  padding: withVar(SpacingToken.Spacing6),
  backgroundColor: withVar(DesignToken.SurfaceSecondaryDefault),
  borderRadius: withVar(RadiusToken.Radius3),
  marginBottom: withVar(SpacingToken.Spacing2),
  width: "100%",
  maxWidth: `calc(100% - ${withVar(SpacingToken.Spacing6)})`,
  marginLeft: withVar(SpacingToken.Spacing6),
};

const addButtonStyles = { marginTop: withVar(SpacingToken.Spacing4), width: "fit-content" };
